'use client';

import { useEffect, useState, useRef } from 'react';

export interface VideoUrls {
  recordingUrl: string | null;
  thumbnailUrl: string | null;
}

export interface UseVideoUrlsOptions {
  /** Function to fetch the video and thumbnail URLs */
  fetchUrls: () => Promise<VideoUrls>;
  /** Dependencies that should trigger a refetch */
  deps?: any[];
}

export interface UseVideoUrlsReturn {
  /** The video recording URL */
  recordingUrl: string | null;
  /** The thumbnail image URL */
  thumbnailUrl: string | null;
  /** Whether URLs are currently being fetched */
  loading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Manually refetch the URLs */
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching video recording and thumbnail URLs.
 * Handles loading state, errors, and supports refetching.
 */
export function useVideoUrls(options: UseVideoUrlsOptions): UseVideoUrlsReturn {
  const { fetchUrls, deps = [] } = options;

  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Keep fetchUrls ref up to date to avoid stale closures
  const fetchUrlsRef = useRef(fetchUrls);
  useEffect(() => {
    fetchUrlsRef.current = fetchUrls;
  }, [fetchUrls]);

  const doFetch = async () => {
    setLoading(true);
    setError(null);
    try {
      const { recordingUrl: url, thumbnailUrl: thumb } =
        await fetchUrlsRef.current();
      setRecordingUrl(url);
      setThumbnailUrl(thumb);
    } catch (err) {
      console.error('[useVideoUrls] Error fetching URLs:', err);
      setError(
        err instanceof Error ? err : new Error('Failed to fetch video URLs')
      );
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and refetch on deps change
  useEffect(() => {
    doFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return {
    recordingUrl,
    thumbnailUrl,
    loading,
    error,
    refetch: doFetch
  };
}
